// Scope-walking + snapshot-lookup helpers shared by resolveIdentifier.
// Pure functions; each walks the parent chain of a Scope to find the first
// match. Split from resolveIdentifier.ts to keep both files under the 200-
// line budget.
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Identifier Resolver.

import type { QualifiedName } from '../../sql-grammar/model';
import type {
  AliasBinding,
  CteBinding,
  ReachableTable,
  SchemaSnapshot,
  Scope,
} from '../types';
import { lookupRelationColumns, lookupRelationTable, reachableRelation } from '../service/snapshotRelations';

export function walkAliases(scope: Scope, folded: string): AliasBinding | undefined {
  let s: Scope | undefined = scope;
  while (s) {
    const hit = s.aliases.find((a) => a.aliasFolded === folded);
    if (hit) return hit;
    s = s.parent;
  }
  return undefined;
}

export function walkCtes(scope: Scope, folded: string): CteBinding | undefined {
  let s: Scope | undefined = scope;
  while (s) {
    const hit = s.ctes.find((c) => c.name.foldedName === folded);
    if (hit) return hit;
    s = s.parent;
  }
  return undefined;
}

export function walkReachableTables(scope: Scope, folded: string): ReachableTable | undefined {
  return walkReachableTablesScoped(scope, folded, undefined);
}

export function walkReachableTablesScoped(
  scope: Scope,
  nameFolded: string,
  schemaFolded: string | undefined,
): ReachableTable | undefined {
  let s: Scope | undefined = scope;
  while (s) {
    const hit = s.reachableTables.find((r) => {
      if (r.qualified.foldedName !== nameFolded) return false;
      if (schemaFolded === undefined) return true;
      return r.qualified.foldedSchema === schemaFolded;
    });
    if (hit) return hit;
    s = s.parent;
  }
  return undefined;
}

export function findColumnInRelation(
  snap: SchemaSnapshot | undefined,
  relation: ReachableTable,
  folded: string,
): QualifiedName | undefined {
  if (!snap) return undefined;
  const cols = lookupRelationColumns(snap, reachableRelation(relation));
  if (!cols) return undefined;
  const hit = cols.find((c) => c.qualified.foldedName === folded);
  return hit?.qualified;
}

export function resolveRelationLabel(
  snap: SchemaSnapshot | undefined,
  qn: QualifiedName,
  fallback: 'table' | 'view',
): 'table' | 'view' {
  if (!snap) return fallback;
  const t = lookupRelationTable(snap, qn);
  if (!t) return fallback;
  return t.kind === 'view' ? 'view' : 'table';
}
